import { Tokenizer, type UnitConversionToken, unitsToString } from './tokenizer.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';
import type { UsageUnits } from '../../types/db.ts';
import {
  dateToHalfYearEnd,
  dateToHalfYearStart,
  dateToMonthEnd,
  dateToMonthStart,
  dateToQuarterEnd,
  dateToQuarterStart,
  dateToWeekEnd,
  dateToWeekStart,
  dateToYear,
  dateToYearAndMonth,
  dateToYearEnd,
  dateToYearStart,
} from '../../../cross-app-shared/services/dates-and-durations.ts';

class Dimension {
  public name: string;
  constructor(name: string) {
    this.name = name;
  }
}

type DimensionSpecifier = { numerator: [Dimension, number][]; denominator: [Dimension, number][] };

export class UnitConversionError {
  readonly message: string;
  constructor(message: string) {
    this.message = message;
  }
}

class Unit {
  public cachedConversionFactors = new Map<Unit, number>();
  public names: string[];
  public inTermsOf: null | { unit: Unit; multiplySelfBy: number } | { unit: Unit; divideSelfBy: number };
  public dimension: Dimension | null;

  constructor(
    names: string[],
    inTermsOf: null | { unit: Unit; multiplySelfBy: number } | { unit: Unit; divideSelfBy: number },
    dimension: Dimension | null = inTermsOf ? inTermsOf.unit.dimension : null,
  ) {
    this.names = names;
    this.inTermsOf = inTermsOf;
    this.dimension = dimension;
  }

  longName(): string {
    let longest = this.names[0];
    for (const name of this.names) {
      if (name.length > longest.length) {
        longest = name;
      }
    }
    return longest;
  }

  shortName(): string {
    let shortest = this.names[0];
    for (const name of this.names) {
      if (name.length < shortest.length) {
        shortest = name;
      }
    }
    return shortest;
  }

  private inTermsOfConvert(value: number): number {
    if ('multiplySelfBy' in this.inTermsOf!) {
      return value * this.inTermsOf.multiplySelfBy;
    } else {
      return value / this.inTermsOf!.divideSelfBy;
    }
  }

  private inTermsOfConvertReverse(value: number): number {
    if ('multiplySelfBy' in this.inTermsOf!) {
      return value / this.inTermsOf.multiplySelfBy;
    } else {
      return value * this.inTermsOf!.divideSelfBy;
    }
  }

  baseUnit(): Unit {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let current: Unit = this;
    while (current.inTermsOf) {
      current = current.inTermsOf.unit;
    }

    return current;
  }

  private pathFromBaseUnit(): Unit[] {
    const result: Unit[] = [this];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let current: Unit = this;
    while (current.inTermsOf) {
      result.push(current.inTermsOf.unit);
      current = current.inTermsOf.unit;
    }
    result.pop(); // Remove base unit.

    return result.reverse();
  }

  convertTo(other: Unit, value: number): number | UnitConversionError {
    if (this === other) {
      return value;
    }

    if (this.inTermsOf?.unit === other) {
      return this.inTermsOfConvert(value);
    }

    const cachedFactor = this.cachedConversionFactors.get(other);
    if (cachedFactor !== undefined) {
      return value * cachedFactor;
    }

    if (this.dimension !== other.dimension) {
      return new UnitConversionError(
        `Cannot convert ${value} ${this.names[0]} (${this.dimension?.name ?? 'dimensionless'}) to ${other.names[0]} (${other.dimension?.name ?? 'dimensionless'})`,
      );
    }

    let current: { unit: Unit; factor: number } = { unit: this, factor: 1 };
    // Work our way back to the base unit:
    while (current.unit.inTermsOf) {
      current = { unit: current.unit.inTermsOf.unit, factor: current.unit.inTermsOfConvert(current.factor) };
    }

    // And then apply conversions to get from base unit to destination unit:
    for (const unit of other.pathFromBaseUnit()) {
      current = { unit, factor: unit.inTermsOfConvertReverse(current.factor) };
    }

    this.cachedConversionFactors.set(other, current.factor);
    return value * current.factor;
  }
}
export type { Unit };

class UnknownUnit extends Unit {
  private static unknownUnitsCache = new Map<string, UnknownUnit>();
  static create(name: string): UnknownUnit {
    const existing = this.unknownUnitsCache.get(name);
    if (existing) {
      return existing;
    }

    const newUnknown = new UnknownUnit(name);
    this.unknownUnitsCache.set(name, newUnknown);
    return newUnknown;
  }

  private constructor(name: string) {
    super([name], null, Unknown);
  }

  convertTo(): number | UnitConversionError {
    return new UnitConversionError(`${this.names[0]} is not a known unit, and cannot be converted.`);
  }
}

class CompoundUnit {
  public names: string[];
  public units: {
    // More complex units are possible, but I'm not sure that we will define them (we'll see):
    numerator: { unit: Unit; exponent: number; multiplySelfBy: number };
    // denominator: { unit: Unit; exponent: number; multiplySelfBy: number }[];
  };
  public dimensions: DimensionSpecifier;

  constructor(
    names: string[],
    units: {
      // More complex units are possible, but I'm not sure that we will define them (we'll see):
      numerator: { unit: Unit; exponent: number; multiplySelfBy: number };
      // denominator: { unit: Unit; exponent: number; multiplySelfBy: number }[];
    },
    dimensions: DimensionSpecifier,
  ) {
    this.names = names;
    this.units = units;
    this.dimensions = dimensions;
  }

  longName(): string {
    let longest = this.names[0];
    for (const name of this.names) {
      if (name.length > longest.length) {
        longest = name;
      }
    }
    return longest;
  }

  shortName(): string {
    let shortest = this.names[0];
    for (const name of this.names) {
      if (name.length < shortest.length) {
        shortest = name;
      }
    }
    return shortest;
  }

  convertToBaseUnit(value: number): { value: number; unit: Unit; exponent: number } {
    const { unit, exponent, multiplySelfBy } = this.units.numerator;
    return {
      value: value * multiplySelfBy,
      unit,
      exponent,
    };
  }

  convertTo(other: CompoundUnit, value: number): number | UnitConversionError {
    return new UnitsTracker(new Map([[this, 1]])).convertTo(new UnitsTracker(new Map([[other, 1]])), value);
  }
}
export type { CompoundUnit };

export interface CalendarRange {
  calendarType: CalendarType;
  start: ISO8601DateOnly;
  end: ISO8601DateOnly;
  // date ==> 2024-01-01
  // week ==> {2023-12-31}-{2024-01-06}
  // month ==> 2024-01
  // quarter ==> {2024-01-01}-{2024-03-31}
  // halfYear ==> {2024-01-01}-{2024-06-30}
  // year ==> 2024
  key: string;
}

export class UnitsTracker {
  static fromUnitConversionToken(units: UnitConversionToken['units']): UnitsTracker {
    const exponents = new Map<Unit | CompoundUnit, number>();

    for (const { unit: unitStr, exponent } of units) {
      const unit = unitsByName.get(unitStr) ?? UnknownUnit.create(unitStr);
      exponents.set(unit, (exponents.get(unit) ?? 0) + exponent);
    }

    return new UnitsTracker(exponents);
  }

  private static parsedUnitsCache = new Map<string, UnitsTracker>();
  static fromParsedUnitString(unitsStr: string): UnitsTracker {
    const existing = this.parsedUnitsCache.get(unitsStr);
    if (existing) {
      return existing;
    }

    const unitTokenOrError = Tokenizer.tokenizeUnits(unitsStr);
    if ('error' in unitTokenOrError) {
      console.error('UnitsTracker.fromParsedUnitString', unitTokenOrError.error);
      const result = UnitsTracker.fromSingleUnit(UnknownUnit.create(unitsStr));
      this.parsedUnitsCache.set(unitsStr, result);
      return result;
    }

    const result = UnitsTracker.fromUnitConversionToken(unitTokenOrError.units);
    this.parsedUnitsCache.set(unitsStr, result);
    return result;
  }

  static fromSingleUnit(unit: Unit | CompoundUnit): UnitsTracker {
    return new UnitsTracker(new Map([[unit, 1]]));
  }

  static unitless() {
    return new UnitsTracker(new Map());
  }

  static canConvert({ from, to }: { from: string; to: string }): boolean {
    const base = UnitsTracker.fromParsedUnitString(from);
    const candidate = UnitsTracker.fromParsedUnitString(to);
    const converted = base.convertTo(candidate, 1);
    return typeof converted === 'number';
  }

  public readonly exponents: ReadonlyMap<Unit | CompoundUnit, number>;
  constructor(exponents: Map<Unit | CompoundUnit, number>) {
    this.exponents = exponents;
  }

  isUnknown(): boolean {
    for (const unit of this.exponents.keys()) {
      if (unit instanceof UnknownUnit) {
        return true;
      }
    }

    return false;
  }

  isScalar(): boolean {
    if (this.exponents.size === 0) {
      return true;
    } else if (this.exponents.size === 1) {
      const [[unit, exponent]] = Array.from(this.exponents.entries());
      return exponent === 1 && unit instanceof Unit && unit.baseUnit() === Scalar;
    }

    return false;
  }

  isPounds(): boolean {
    return this.exponents.size === 1 && this.exponents.get(Pound) === 1;
  }

  isPercent(): boolean {
    return this.exponents.size === 1 && this.exponents.get(Percent) === 1;
  }

  toCalendarType(): null | CalendarType {
    if (this.exponents.size !== 1) {
      return null;
    }

    const unit = Array.from(this.exponents.keys())[0];
    if (unit === CalendarDate) {
      return 'date';
    } else if (unit === CalendarWeek) {
      return 'week';
    } else if (unit === CalendarMonth) {
      return 'month';
    } else if (unit === CalendarQuarter) {
      return 'quarter';
    } else if (unit === CalendarHalfYear) {
      return 'halfYear';
    } else if (unit === CalendarYear) {
      return 'year';
    } else {
      return null;
    }
  }

  equals(other: UnitsTracker): boolean {
    if (this.exponents.size !== other.exponents.size) {
      return false;
    }

    for (const [thisUnit, thisExponent] of this.exponents.entries()) {
      if (other.exponents.get(thisUnit) !== thisExponent) {
        return false;
      }
    }

    return true;
  }

  isConcentration(): boolean {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 1) return false;
    const unit = Array.from(simplified.exponents.keys())[0];
    if (!(unit instanceof Unit)) return false;
    // Percent, ppm, ppb all have Scalar as their base unit and are not Scalar itself.
    return unit !== Scalar && unit.baseUnit() === Scalar;
  }

  /**
   * Whether a value in these units is one quantity per another (lb/gal, lb/MMBtu, %, ppm), so that adding two up is
   * meaningless. A quantity per unit of time (lb/hr, ton/yr) is a rate rather than a ratio: rates from two sources
   * running side by side do add up, so they are not counted here.
   */
  isRatio(): boolean {
    if (this.isConcentration()) {
      return true;
    }

    for (const [unit, exponent] of this.simplify().tracker.exponents as ReadonlyMap<Unit, number>) {
      if (exponent < 0 && unit.dimension !== Duration && unit.dimension !== Calendar) {
        return true;
      }
    }

    return false;
  }

  isLength(): boolean {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 1) {
      return false;
    }

    const [unit, exponent] = Array.from(simplified.exponents.entries() as MapIterator<[Unit, number]>)[0];

    return unit instanceof Unit && unit.dimension === Length && exponent === 1;
  }

  isMass(): boolean {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 1) {
      return false;
    }

    const unit = Array.from(simplified.exponents.keys())[0];
    if (!(unit instanceof Unit)) {
      return false;
    }

    return unit.dimension === Mass;
  }

  isArea(): boolean {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 1) {
      return false;
    }

    const [unit, exponent] = Array.from(simplified.exponents.entries() as MapIterator<[Unit, number]>)[0];

    return unit instanceof Unit && unit.dimension === Length && exponent === 2;
  }

  isVolume(): boolean {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 1) {
      return false;
    }

    const [unit, exponent] = Array.from(simplified.exponents.entries() as MapIterator<[Unit, number]>)[0];

    return unit instanceof Unit && unit.dimension === Length && exponent === 3;
  }

  isDensity() {
    const simplified = this.simplify().tracker;
    if (simplified.exponents.size !== 2) {
      return false;
    }

    // Density is Mass / Length^3:
    let foundMass = false;
    let foundLength = false;
    for (const [unit, exponent] of simplified.exponents.entries()) {
      if (unit instanceof Unit && unit.dimension === Mass && exponent === 1) {
        foundMass = true;
      } else if (unit instanceof Unit && unit.dimension === Length && exponent === -3) {
        foundLength = true;
      }
    }

    return foundMass && foundLength;
  }

  convertToScalarIfPercentage(value: number): false | number {
    if (this.exponents.size === 1) {
      const [[unit, exponent]] = Array.from(this.exponents.entries());
      if (exponent === 1 && unit instanceof Unit && unit.baseUnit() === Scalar) {
        const result = unit.convertTo(Scalar, value);
        if (typeof result === 'number') {
          return result;
        } else {
          return false;
        }
      }
    }

    return false;
  }

  canAddOrSubtract(other: UnitsTracker): boolean {
    const unitsActive = new Set([...this.exponents.keys(), ...other.exponents.keys()]);
    for (const unit of unitsActive) {
      const thisExponent = this.exponents.get(unit) ?? 0;
      const otherExponent = other.exponents.get(unit) ?? 0;

      if (thisExponent !== otherExponent) {
        return false;
      }
    }

    return true;
  }

  multiply(other: UnitsTracker): UnitsTracker {
    const newExponents = new Map<Unit | CompoundUnit, number>();
    const unitsActive = new Set([...this.exponents.keys(), ...other.exponents.keys()]);
    for (const unit of unitsActive) {
      const thisExponent = this.exponents.get(unit) ?? 0;
      const otherExponent = other.exponents.get(unit) ?? 0;
      const added = thisExponent + otherExponent;
      if (added !== 0) {
        newExponents.set(unit, added);
      }
    }

    return new UnitsTracker(newExponents);
  }

  divide(other: UnitsTracker): UnitsTracker {
    const newExponents = new Map<Unit | CompoundUnit, number>();
    const unitsActive = new Set([...this.exponents.keys(), ...other.exponents.keys()]);
    for (const unit of unitsActive) {
      const thisExponent = this.exponents.get(unit) ?? 0;
      const otherExponent = other.exponents.get(unit) ?? 0;
      const subtracted = thisExponent - otherExponent;
      if (subtracted !== 0) {
        newExponents.set(unit, subtracted);
      }
    }

    return new UnitsTracker(newExponents);
  }

  power(other: UnitsTracker, count: number): UnitsTracker | UnitConversionError {
    if (!other.isScalar()) {
      return new UnitConversionError(`Cannot raise a number to a non-scalar power (${other.toString()})`);
    }

    const newExponents = new Map<Unit | CompoundUnit, number>();
    for (const unit of this.exponents.keys()) {
      const thisExponent = this.exponents.get(unit) ?? 0;
      const multiplied = thisExponent * count;
      if (multiplied !== 0) {
        newExponents.set(unit, multiplied);
      }
    }

    return new UnitsTracker(newExponents);
  }

  /**
   * Returns `true` if all exponents are provided against `Unit`s, returns `false` if any `CompoundUnit`s are present.
   */
  private isAllBaseUnits(): boolean {
    if (this.exponents.size === 0) {
      return false;
    }

    return Array.from(this.exponents.keys()).every((u) => u instanceof Unit);
  }

  /**
   * If the UnitsTracker contains `CompoundUnit`s this will return a new tracker with those simplified into their
   * base `Unit`s.
   */
  private simplify(): { tracker: UnitsTracker; conversionFactor: number } {
    if (this.isAllBaseUnits()) {
      return { tracker: this, conversionFactor: 1 };
    }

    const newExponents = new Map<Unit, number>();
    let conversionFactor = 1;

    for (let [unit, exponent] of this.exponents.entries()) {
      if (unit instanceof CompoundUnit) {
        const converted = unit.convertToBaseUnit(1);
        unit = converted.unit;
        conversionFactor *= converted.value ** exponent;
        exponent = converted.exponent * exponent;
      }

      newExponents.set(unit, (newExponents.get(unit) ?? 0) + exponent);
    }

    return { tracker: new UnitsTracker(newExponents), conversionFactor };
  }

  convertTo(other: UnitsTracker, value: number): number | UnitConversionError {
    const thisSimple = this.simplify();
    const otherSimple = other.simplify();

    if (thisSimple.tracker.exponents.size !== otherSimple.tracker.exponents.size) {
      return new UnitConversionError(
        `Converting from ${this.toString()} to ${other.toString()} is not supported (mismatched unit counts).`,
      );
    }

    const otherUnitsUsed = new Set<Unit>();
    const otherEntries = Array.from(otherSimple.tracker.exponents.entries() as MapIterator<[Unit, number]>);
    let conversionFactor = thisSimple.conversionFactor / otherSimple.conversionFactor;

    for (const [thisUnit, thisExponent] of thisSimple.tracker.exponents.entries() as MapIterator<[Unit, number]>) {
      const maybeOther = otherEntries.find(
        (e) => !otherUnitsUsed.has(e[0]) && thisUnit.dimension === e[0].dimension && thisExponent === e[1],
      );

      if (!maybeOther) {
        return new UnitConversionError(
          `Converting from ${this.toString()} to ${other.toString()} is not supported (cannot find match for ${thisUnit.longName()}).`,
        );
      }

      const [otherUnit] = maybeOther;
      otherUnitsUsed.add(otherUnit);
      const converted = thisUnit.convertTo(otherUnit, 1);
      if (typeof converted !== 'number') {
        return converted;
      }
      conversionFactor *= converted ** thisExponent;
    }

    return value * conversionFactor;
  }

  /**
   * The net exponent of each dimension these units are made of, keyed by dimension name, e.g. lb/hr is
   * `{ Mass: 1, Duration: -1 }` and gal is `{ Length: 3 }`. Units without a dimension (%, ppm) count as `Dimensionless`.
   */
  dimensionExponents(): Map<string, number> {
    const result = new Map<string, number>();
    for (const [unit, exponent] of this.simplify().tracker.exponents as ReadonlyMap<Unit, number>) {
      const name = unit.dimension?.name ?? 'Dimensionless';
      const summed = (result.get(name) ?? 0) + exponent;
      if (summed === 0) {
        result.delete(name);
      } else {
        result.set(name, summed);
      }
    }

    return result;
  }

  toDimensionString() {
    const simplified = this.simplify().tracker;
    const numerator: string[] = [];
    const denominator: string[] = [];

    for (const [unit, exponent] of simplified.exponents.entries()) {
      let name = (unit as Unit).dimension?.name;
      if (!name) {
        continue;
      }
      const absExponent = Math.abs(exponent);
      if (name === 'Length' && absExponent === 2) {
        name = 'Area';
      } else if (name === 'Length' && absExponent === 3) {
        name = 'Volume';
      } else if (absExponent !== 1) {
        name += '^' + absExponent;
      }

      if (exponent > 0) {
        numerator.push(name);
      } else {
        denominator.push(name);
      }
    }

    if (numerator.length > 0 && denominator.length > 0) {
      return numerator.join('-') + '/' + denominator.join('-');
    } else if (numerator.length > 0) {
      return numerator.join('-');
    } else if (denominator.length > 0) {
      return denominator.join('-');
    } else {
      return 'No Dimensions';
    }
  }

  toString() {
    return unitsToString(
      Array.from(this.exponents.entries()).map(([unit, exponent]) => ({ unit: unit.shortName(), exponent })),
    );
  }
}

export function dateConvertToCalendarRange(
  date: ISO8601DateOnly,
  type: CalendarType,
): CalendarRange | UnitConversionError {
  if (type === 'date') {
    return {
      calendarType: 'date',
      start: date,
      end: date,
      key: date,
    };
  } else if (type === 'week') {
    const start = dateToWeekStart(date);
    const end = dateToWeekEnd(date);
    return {
      calendarType: 'week',
      start,
      end,
      key: `${start}-${end}`,
    };
  } else if (type === 'month') {
    const start = dateToMonthStart(date);
    const end = dateToMonthEnd(date);
    return {
      calendarType: 'month',
      start,
      end,
      key: dateToYearAndMonth(start),
    };
  } else if (type === 'quarter') {
    const start = dateToQuarterStart(date);
    const end = dateToQuarterEnd(date);
    return {
      calendarType: 'quarter',
      start,
      end,
      key: `${start}-${end}`,
    };
  } else if (type === 'halfYear') {
    const start = dateToHalfYearStart(date);
    const end = dateToHalfYearEnd(date);
    return {
      calendarType: 'halfYear',
      start,
      end,
      key: `${start}-${end}`,
    };
  } else if (type === 'year') {
    const start = dateToYearStart(date);
    const end = dateToYearEnd(date);
    return {
      calendarType: 'year',
      start,
      end,
      key: dateToYear(date).toString(),
    };
  } else {
    const unexpected: never = type;
    return new UnitConversionError(`Unexpected calendar value type: ${unexpected}`);
  }
}

// Rank determines which conversions are valid: source rank must be <= destination rank.
// Week is omitted because weeks straddle boundaries of other calendar units.
const calendarRank: Record<Exclude<CalendarType, 'week'>, number> = {
  date: 0,
  month: 1,
  quarter: 2,
  halfYear: 3,
  year: 4,
};

export const calendarLabel: Record<CalendarType, string> = {
  date: 'Dates',
  week: 'Weeks',
  month: 'Months',
  quarter: 'Quarters',
  halfYear: 'Half years',
  year: 'Years',
};

export const calendarTypeToUnitName: Record<CalendarType, string> = {
  date: 'caldate',
  week: 'calweek',
  month: 'calmonth',
  quarter: 'calquarter',
  halfYear: 'calhalfyear',
  year: 'calyear',
};

export const calendarTypesAll = Object.keys(calendarTypeToUnitName) as CalendarType[];

/**
 * If this function returns `{ valid: true }` any date in a source range can be safely converted to a destination range
 * by calling `dateConvertToCalendarRange` with any date in that source range.
 */
export function dateRangeConversionIsValid(
  sourceType: CalendarType,
  destinationType: CalendarType,
): { valid: true } | { valid: false; explanation: string } {
  // No conversion needs to be performed, so it's always valid:
  if (sourceType === destinationType) {
    return { valid: true };
  }

  // A single date always fits cleanly into any calendar period:
  if (sourceType === 'date') {
    return { valid: true };
  }

  // Weeks straddle boundaries of other calendar units, and so cannot be converted:
  if (sourceType === 'week' || destinationType === 'week') {
    return {
      valid: false,
      explanation:
        sourceType === 'week'
          ? `Weeks may straddle the boundaries between calendar units, and so cannot be reliably converted to ${calendarLabel[destinationType]}.`
          : `${calendarLabel[sourceType]} cannot be converted to Weeks.`,
    };
  }

  // Smaller types can always be converted into bigger types:
  if (calendarRank[sourceType] <= calendarRank[destinationType]) {
    return { valid: true };
  }

  // But the other way around doesn't work:
  return {
    valid: false,
    explanation: `${calendarLabel[sourceType]} cannot be converted to ${calendarLabel[destinationType]}.`,
  };
}

export const Unknown = new Dimension('Unknown');
export const Length = new Dimension('Length');
export const Area: DimensionSpecifier = { numerator: [[Length, 2]], denominator: [] };
export const Volume: DimensionSpecifier = { numerator: [[Length, 3]], denominator: [] };
export const Duration = new Dimension('Duration');
export const Calendar = new Dimension('Calendar');
export const Mass = new Dimension('Mass');
export const Energy = new Dimension('Energy');

export const Scalar = new Unit(['Scalar'], null);
export const Percent = new Unit(['%', 'percent', 'percentage', 'pct'], { unit: Scalar, divideSelfBy: 100 });
export const PartsPerMillion = new Unit(['ppm'], { unit: Scalar, divideSelfBy: 1_000_000 });
export const PartsPerBillion = new Unit(['ppb'], { unit: Scalar, divideSelfBy: 1_000_000_000 });

// SI units:
export const Meter = new Unit(['m', 'meter', 'meters'], null, Length);
export const Kilometer = new Unit(['km', 'kilometer', 'kilometers'], { unit: Meter, multiplySelfBy: 1000 });
export const Centimeter = new Unit(['cm', 'centimeter', 'centimeters'], { unit: Meter, divideSelfBy: 100 });
export const Decimeter = new Unit(['dm', 'decimeter', 'decimeters'], { unit: Meter, divideSelfBy: 10 });
export const SquareKilometer = new CompoundUnit(
  ['squarekilometer', 'sqkm'],
  {
    numerator: { unit: Kilometer, exponent: 2, multiplySelfBy: 1 },
  },
  Area,
);

// Everything else in this file is close to a physical quantity, so these are a bit odd-man-out as conversions
// between these items is often not possible. That said, our formula system does want to be able to treat these in the
// same way we do other units (i.e. unit annotations and conversions: `'2025-01-02' date -> calweek`), so it seems to be
// a good enough fit:
export const CalendarDate = new Unit(['calendardate', 'caldate', 'date'], null, Calendar);
export const CalendarWeek = new Unit(['calendarweek', 'calweek'], null, Calendar);
export const CalendarMonth = new Unit(['calendarmonth', 'calmonth'], null, Calendar);
export const CalendarQuarter = new Unit(['calendarquarter', 'calquarter'], null, Calendar);
export const CalendarHalfYear = new Unit(['calendarhalfyear', 'calhalfyear'], null, Calendar);
export const CalendarYear = new Unit(['calendaryear', 'calyear'], null, Calendar);
// Elsewhere in the system we'll want to refer to these more approachable names:
export type CalendarType = 'year' | 'halfYear' | 'quarter' | 'month' | 'week' | 'date';

export const Second = new Unit(['s', 'second', 'seconds', 'sec'], null, Duration);
export const Minute = new Unit(['min', 'minute', 'minutes'], { unit: Second, multiplySelfBy: 60 });
export const Hour = new Unit(['h', 'hr', 'hour', 'hours'], { unit: Minute, multiplySelfBy: 60 });
export const Day = new Unit(['day', 'days', 'd'], { unit: Hour, multiplySelfBy: 24 });
export const Week = new Unit(['week', 'weeks', 'wk'], { unit: Day, multiplySelfBy: 7 });
export const Month = new Unit(['month', 'months', 'mo'], { unit: Day, multiplySelfBy: 30 });
export const Year = new Unit(['year', 'years', 'yr'], { unit: Day, multiplySelfBy: 365 });

export const Gram = new Unit(['g', 'gram', 'grams'], null, Mass);
export const Milligram = new Unit(['mg', 'milligram', 'milligrams'], { unit: Gram, divideSelfBy: 1000 });
export const Kilogram = new Unit(['kg', 'kilogram', 'kilograms'], { unit: Gram, multiplySelfBy: 1000 });
export const MetricTon = new Unit(['tonne', 'tonnes', 'metricton', 'metrictons'], {
  unit: Gram,
  multiplySelfBy: 1_000_000,
});

export const Joule = new Unit(['J', 'joule', 'joules'], null, Energy);

export const Liter = new CompoundUnit(
  ['L', 'l', 'liter', 'liters'],
  {
    numerator: { unit: Decimeter, exponent: 3, multiplySelfBy: 1 },
  },
  Volume,
);

// Imperial units:
export const Inch = new Unit(['in', 'inch', 'inches'], { unit: Meter, multiplySelfBy: 0.0254 });
export const Foot = new Unit(['ft', 'foot', 'feet'], { unit: Inch, multiplySelfBy: 12 });
export const Yard = new Unit(['yd', 'yard', 'yards'], { unit: Foot, multiplySelfBy: 3 });
export const Mile = new Unit(['miles', 'mile', 'mi'], { unit: Foot, multiplySelfBy: 5280 });
export const SquareMile = new CompoundUnit(
  ['squaremile', 'sqmi'],
  {
    numerator: { unit: Mile, exponent: 2, multiplySelfBy: 1 },
  },
  Area,
);
export const SquareFoot = new CompoundUnit(
  ['squarefoot', 'sqft'],
  {
    numerator: { unit: Foot, exponent: 2, multiplySelfBy: 1 },
  },
  Area,
);
export const Acre = new CompoundUnit(
  ['acre', 'acres'],
  { numerator: { unit: Yard, exponent: 2, multiplySelfBy: 4840 } },
  Area,
);

export const Grain = new Unit(['grain', 'grains'], { unit: Milligram, multiplySelfBy: 64.79891 });
export const Pound = new Unit(['lbs', 'pound', 'pounds', 'lb'], { unit: Grain, multiplySelfBy: 7000 });
export const Ounce = new Unit(['ounce', 'ounces', 'oz'], { unit: Pound, divideSelfBy: 16 });
export const Ton = new Unit(['ton', 'tons', 'shortton', 'shorttons'], { unit: Pound, multiplySelfBy: 2000 });
export const Hundredweight = new Unit(['cwt', 'hundredweight'], { unit: Pound, multiplySelfBy: 100 });

export const Btu = new Unit(['BTU', 'btu', 'Btu'], { unit: Joule, multiplySelfBy: 1055.05585262 });
export const MMBtu = new Unit(['mmbtu', 'MMBtu'], { unit: Btu, multiplySelfBy: 1_000_000 });
export const Therm = new Unit(['therm', 'therms', 'Therm', 'Therms'], { unit: Btu, multiplySelfBy: 100_000 });
// MMscf and MMcf are both actually volume measurements (a million cubic feet), but all our users universally use it to
// refer to natural gas, and so it is much more helpful to think of it as a unit of energy, as they often also think
// of natural gas in terms of energy content (Btu or therms).
// Technically MMscf and MMcf could differ (the "s" means "standard conditions"), but generally standard conditions are
// assumed.
// So, we use 1 MMscf = 1 MMcf ≈ 1,020 MMBtu at standard heat content of 1,020 BTU/scf
// If we ever onboard an industry that uses MMcf for something other than natural gas, we'll need to revisit this, as
// we could calculate incorrect results, or we may incorrectly throw an error when trying to convert this "energy"
// measurement to some other kind of volume:
export const MMSCF = new Unit(['MMSCF', 'MMscf', 'mmscf', 'MMcf', 'mmcf', 'MMCF'], {
  unit: Btu,
  multiplySelfBy: 1_020_000_000,
});

export const CubicFoot = new CompoundUnit(
  ['cuft', 'cubicfoot', 'cubicfeet'],
  {
    numerator: { unit: Foot, exponent: 3, multiplySelfBy: 1 },
    // denominator: [],
  },
  Volume,
);
export const Gallon = new CompoundUnit(
  ['gal', 'gallon', 'gallons'],
  {
    numerator: { unit: Inch, exponent: 3, multiplySelfBy: 231 },
    // denominator: [],
  },
  Volume,
);
export const FluidOunce = new CompoundUnit(
  ['floz', 'fluidounce', 'fluidounces'],
  {
    numerator: { unit: Inch, exponent: 3, multiplySelfBy: 231 / 128 },
    // denominator: [],
  },
  Volume,
);

export const simpleUnits: Unit[] = [
  Scalar,
  Percent,
  PartsPerMillion,
  PartsPerBillion,

  Meter,
  Kilometer,
  Centimeter,
  Decimeter,

  Second,
  Minute,
  Hour,
  Day,
  Week,
  Month,
  Year,

  CalendarYear,
  CalendarHalfYear,
  CalendarQuarter,
  CalendarMonth,
  CalendarWeek,
  CalendarDate,

  Gram,
  Milligram,
  Kilogram,
  MetricTon,

  Joule,
  Btu,
  MMBtu,
  Therm,
  MMSCF,

  Inch,
  Foot,
  Yard,
  Mile,

  Grain,
  Pound,
  Ounce,
  Ton,
  Hundredweight,
];

export const compoundUnits: CompoundUnit[] = [
  SquareKilometer,
  SquareFoot,
  SquareMile,
  Acre,
  Liter,
  CubicFoot,
  Gallon,
  FluidOunce,
];

const unitsByName = new Map<string, Unit | CompoundUnit>([
  ...simpleUnits.flatMap((unit): [string, Unit | CompoundUnit][] => unit.names.map((name) => [name, unit])),
  ...compoundUnits.flatMap((unit): [string, Unit | CompoundUnit][] => unit.names.map((name) => [name, unit])),
]);

export const massUnitsForUsageUnits: string[] = [
  'pounds',
  'grams',
  'milligrams',
  'kilograms',
  'metrictons',
  'grains',
  'ounces',
  'tons',
  'cwt',
];

export const areaUnitsForUsageUnits: string[] = ['sqft', 'sqkm', 'sqmi', 'acres'];

export const concentrationUnitsForUsageUnits: string[] = ['pct', 'ppm', 'ppb'];

export const lengthUnitsForUsageUnits: string[] = ['inches', 'feet', 'yards', 'miles', 'meters', 'km', 'cm'];

export const volumeUnitsForUsageUnits: string[] = ['gallons', 'liters', 'cubicfeet', 'fluidounces'];

/** Natural gas units that should be switchable between each other. */
export const gasUnitsForUsageUnits: string[] = ['MMSCF', 'mmbtu', 'therms'];

/** Returns true when the given unit string is a natural gas unit (MMSCF/MMcf, MMBtu, or therms). */
export function isGasUnit(unitStr: string): boolean {
  return (
    MMSCF.names.includes(unitStr) ||
    Therm.names.includes(unitStr) ||
    MMBtu.names.includes(unitStr) ||
    Btu.names.includes(unitStr)
  );
}

/** Pretty-print a unit identifier for display. */
export function unitLabel(value: string): string {
  switch (value) {
    case 'cubicfeet':
      return 'cubic feet';
    case 'fluidounces':
      return 'fluid ounces';
    case 'mmbtu':
      return 'MMBtu';
    case 'therms':
      return 'therms';
    case 'pct':
      return '%';
    case 'ppm':
      return 'ppm';
    case 'ppb':
      return 'ppb';
    case 'sqft':
      return 'sq ft';
    case 'sqkm':
      return 'sq km';
    case 'sqmi':
      return 'sq mi';
    case 'acres':
      return 'acres';
    default:
      return value;
  }
}

/** Given a unit string, returns the list of alternative units it can be converted to. */
export function getAllowedAlternativeUnits(unitStr: string): string[] {
  if (isGasUnit(unitStr)) return gasUnitsForUsageUnits;
  const tracker = UnitsTracker.fromParsedUnitString(unitStr);
  if (tracker.isConcentration()) return concentrationUnitsForUsageUnits;
  if (tracker.isLength()) return lengthUnitsForUsageUnits;
  if (tracker.isArea()) return areaUnitsForUsageUnits;
  if (tracker.isMass()) return massUnitsForUsageUnits;
  if (tracker.isVolume()) return volumeUnitsForUsageUnits;
  return [];
}

/**
 * Units offered for per-vessel capacity entry. A single product may be stored in
 * vessels measured either by volume or by mass (e.g. some tanks in gallons, some
 * drums in pounds), so both dimensions are always offered. The dimension matching
 * the product's usage unit is listed first for familiarity. `usageUnits` acts only
 * as the default selection for new vessel rows.
 */
export function getCapacityUnitOptions(usageUnits: string): string[] {
  const usageIsMass = UnitsTracker.fromParsedUnitString(usageUnits).isMass();
  return usageIsMass
    ? [...massUnitsForUsageUnits, ...volumeUnitsForUsageUnits]
    : [...volumeUnitsForUsageUnits, ...massUnitsForUsageUnits];
}

/** Weight of one gallon of water in pounds; multiply by specific gravity for a product. */
export const POUNDS_PER_GALLON_WATER = 8.345;
const poundsUnitsTracker = UnitsTracker.fromParsedUnitString('pounds');
const gallonsUnitsTracker = UnitsTracker.fromParsedUnitString('gallons');

/**
 * Canonical conversion of a product usage amount (in any mass or volume unit) to
 * pounds. Mass units convert directly; volume units convert to gallons and then
 * use the product's specific gravity.
 */
export function usageAmountToPounds(
  amount: number,
  units: string | UnitsTracker,
  specificGravity: number | null,
): number | UnitConversionError {
  const tracker = typeof units === 'string' ? UnitsTracker.fromParsedUnitString(units) : units;
  if (tracker.isMass()) {
    return tracker.convertTo(poundsUnitsTracker, amount);
  }
  if (tracker.isVolume()) {
    if (!isUsableSpecificGravity(specificGravity)) {
      return new UnitConversionError(`a specific gravity greater than zero is required to convert ${units} to pounds`);
    }
    const gallons = tracker.convertTo(gallonsUnitsTracker, amount);
    if (gallons instanceof UnitConversionError) return gallons;
    return gallons * POUNDS_PER_GALLON_WATER * specificGravity;
  }
  return new UnitConversionError(`the units (${units}) are not supported for this product type`);
}

/**
 * The pounds each usage entry should store once its product's specific gravity changes. Only entries recorded in
 * volume units depend on the density. That is decided by each entry's own units, never the product's current
 * default, since a product's default units can change after entries were recorded.
 */
export function usageEntriesPoundsForSpecificGravity<
  Entry extends { manuallyEnteredValue: number; manuallyEnteredUnits: string },
>(
  entries: readonly Entry[],
  specificGravity: number | null,
): { entry: Entry; poundsUsed: number }[] | UnitConversionError {
  const updates: { entry: Entry; poundsUsed: number }[] = [];
  for (const entry of entries) {
    const tracker = UnitsTracker.fromParsedUnitString(entry.manuallyEnteredUnits);
    if (tracker.isUnknown()) {
      return new UnitConversionError(
        `usage was recorded in unknown units (${entry.manuallyEnteredUnits}), so its pounds cannot be updated`,
      );
    }
    if (!tracker.isVolume()) {
      continue;
    }
    const poundsUsed = usageAmountToPounds(entry.manuallyEnteredValue, tracker, specificGravity);
    if (poundsUsed instanceof UnitConversionError) {
      return new UnitConversionError(
        `unable to convert ${entry.manuallyEnteredValue} ${entry.manuallyEnteredUnits} into pounds: ${poundsUsed.message}`,
      );
    }
    updates.push({ entry, poundsUsed });
  }
  return updates;
}

/** Volume-to-mass conversions must use the product's density, never an assumed water density. */
export function isUsableSpecificGravity(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Mirrors the products_specificGravity check constraints so users see a readable message instead of a
 * database error. Returns null when the product's specific gravity is acceptable.
 */
export function productSpecificGravityValidate(usageUnits: UsageUnits, specificGravity: number | null): string | null {
  if (specificGravity !== null && !isUsableSpecificGravity(specificGravity)) {
    return 'Specific gravity must be a number greater than zero.';
  }
  if (usageUnits === 'gallons' && specificGravity === null) {
    return 'Specific gravity is required for products measured in gallons.';
  }
  return null;
}

/** Inverse of usageAmountToPounds for displaying stored pounds in the user's entry units. */
export function poundsToUsageAmount(
  pounds: number,
  units: string,
  specificGravity: number | null,
): number | UnitConversionError {
  const poundsPerUnit = usageAmountToPounds(1, units, specificGravity);
  return typeof poundsPerUnit === 'number' ? pounds / poundsPerUnit : poundsPerUnit;
}
